/**
 * @fileoverview UnifyVenue page — tabbed layout using a ToggleButtonGroup.
 *
 * The page renders two views selectable via a `ToggleButtonGroup`:
 *
 * - **List** — backed by `MarketVenueMappings` resource.
 *   Records are grouped client-side by `placeId` (all groups expanded by default).
 *   Columns inside each group: Market, Venue ID, Venue, Inserted On, Updated On, Updated By.
 *   No checkboxes, no bulk actions, no create/edit.
 *   See {@link components/ListGroupedView}.
 *
 * - **Master/Detail** — backed by `MasterVenues` resource.
 *   Records are grouped client-side by `clusterCode`.
 *   Each expanded group shows PlaceId sub-groups with one checkbox each.
 *   Selecting 2+ checkboxes enables the **Merge** button.
 *   Merge dialog shows distinct locations; user picks a destination.
 *   See {@link components/MasterGroupedView}.
 *
 * Both views:
 * - Have independent filter state (separate `RaList` instances with their own local state).
 * - Include a **Market** filter (Primary, Vivid, Ticket Network, Go Ticket).
 * - Have auto-search **disabled** by default; the user must press Search.
 */

import * as React from 'react';
import { Title, useStore } from 'react-admin';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import RaList from '../../components/ra-list/RaList';
import RenderControls from '../../components/ra-list/RenderControls';
import ListGroupedView from './components/ListGroupedView';
import MasterGroupedView from './components/MasterGroupedView';

// ---------------------------------------------------------------------------
// Market filter choices
// ---------------------------------------------------------------------------

/** Choices for the Market filter used on both tabs. */
const MARKET_CHOICES = [
    { id: 0,  name: 'Primary'        },
    { id: 1,  name: 'Vivid'          },
    { id: 2,  name: 'Ticket Network' },
    { id: 13, name: 'Go Ticket'      },
];

// ---------------------------------------------------------------------------
// Filter controls — List tab (MarketVenueMappings)
// ---------------------------------------------------------------------------

/**
 * Filter controls for the **List** tab — MarketVenueMappings.
 */
const LIST_FILTER_CONTROLS = [
    { component: 'text',   name: 'marketVenueName_contains',   label: 'Venue Name',   xs: 1.5 },
    { component: 'text',   name: 'googleUnifiedName_contains', label: 'Unified Name', xs: 1.5 },
    { component: 'number', name: 'sourceVenueId',              label: 'Venue ID',     xs: 1.5 },
    { component: 'text',   name: 'clusterCode_contains',       label: 'Cluster Code', xs: 1.5 },
    { component: 'text',   name: 'marketVenueCity_contains',   label: 'City',         xs: 1   },
    { component: 'select', name: 'marketId_eq', label: 'Market', xs: 1.5, choices: MARKET_CHOICES },
];

// ---------------------------------------------------------------------------
// Filter controls — Master/Detail tab (MasterVenues)
// ---------------------------------------------------------------------------

/**
 * Filter controls for the **Master/Detail** tab — MasterVenues.
 */
const MASTER_FILTER_CONTROLS = [
    { component: 'text',   name: 'googleUnifiedName_contains', label: 'Unified Name', xs: 1.5 },
    { component: 'text',   name: 'marketVenueName_contains',   label: 'Venue Name',   xs: 1.5 },
    { component: 'number', name: 'sourceVenueId',              label: 'Venue ID',     xs: 1.5 },
    { component: 'text',   name: 'clusterCode_contains',       label: 'Cluster Code', xs: 1.5 },
    { component: 'text',   name: 'city_contains',              label: 'City',         xs: 1   },
    { component: 'text',   name: 'state_contains',             label: 'State',        xs: 0.8 },
    { component: 'select', name: 'marketId_eq', label: 'Market', xs: 1.5, choices: MARKET_CHOICES },
];

// ---------------------------------------------------------------------------
// UnifyVenueList
// ---------------------------------------------------------------------------

/**
 * React-Admin page for the Unify Venue feature.
 *
 * Renders a **ToggleButtonGroup** with two views:
 *
 * - **List** (`MarketVenueMappings`): grouped by PlaceId, all groups expanded,
 *   new columns (InsertedOn, UpdatedOn, UpdatedBy), no checkboxes/bulk actions.
 *
 * - **Master/Detail** (`MasterVenues`): grouped by ClusterCode, one checkbox
 *   per distinct PlaceId within a group, Merge button and dialog.
 *
 * Both views have independent filter state and auto-search is disabled by default.
 */
const UnifyVenueList: React.FC = () => {
    /** Active tab — persisted to react-admin store so it survives navigation. */
    const [view, setView] = useStore<'list' | 'master'>('unifyVenue.view', 'list');

    const refbtnFilterList   = React.useRef<HTMLButtonElement>(null);
    const refbtnFilterMaster = React.useRef<HTMLButtonElement>(null);

    const [mappingSort] = React.useState<string[]>(['placeId asc', 'marketId asc']);
    const [masterSort] = React.useState<string[]>(['clusterCode asc', 'placeId asc', 'marketId asc']);

    // ── Master panel auto-expand state ────────────────────────────────────────
    const [expandAfterMasterVenueId] = React.useState<number | null>(null);

    return (
        <Box>
            <Title title="Unify Venue - " />

            {/* ── View toggle ─────────────────────────────────────────────────── */}
            <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(_e, val) => { if (val) setView(val); }}
                size="small"
                sx={{ mb: 1 }}
            >
                <ToggleButton value="list">List</ToggleButton>
                <ToggleButton value="master">Master / Detail</ToggleButton>
            </ToggleButtonGroup>

            {/* ── List view (MarketVenueMappings, grouped by PlaceId) ─────────── */}
            <Box sx={{ display: view === 'list' ? 'block' : 'none' }}>
                <RaList
                    storeKey={false}
                    resource="MarketVenueMappings"
                    title="List"
                    perPage={25}
                    sortby={mappingSort}
                    filters={
                        <RenderControls
                            filterInline
                            refFilter={refbtnFilterList}
                            controls={LIST_FILTER_CONTROLS}
                            /* No create action */
                        />
                    }
                >
                    <ListGroupedView />
                </RaList>
            </Box>

            {/* ── Master/Detail view (MasterVenues, grouped by ClusterCode) ───── */}
            <Box sx={{ display: view === 'master' ? 'block' : 'none' }}>
                <RaList
                    storeKey={false}
                    resource="MasterVenues"
                    title="Master / Detail"
                    perPage={25}
                    sortby={masterSort}
                    filters={
                        <RenderControls
                            filterInline
                            refFilter={refbtnFilterMaster}
                            controls={MASTER_FILTER_CONTROLS}
                            /* No create action on the Master/Detail view */
                        />
                    }
                >
                    <MasterGroupedView
                        expandAfterMasterVenueId={expandAfterMasterVenueId}
                        refBtnFilterMaster={refbtnFilterMaster}
                    />
                </RaList>
            </Box>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Page registrations
// ---------------------------------------------------------------------------

/**
 * MarketVenueMappings resource definition — List tab.
 * Create/edit routes removed per spec.
 */
export const MarketVenueMappingsPage = {
    name: 'MarketVenueMappings',
};

/**
 * MasterVenues resource definition — Master/Detail tab.
 * Hosts the combined Unify Venue list page.
 * Create/edit routes removed per spec.
 */
const MasterVenuesPage = {
    name: 'MasterVenues',
    list: UnifyVenueList,
};

export default MasterVenuesPage;
